import React, { useMemo } from 'react'
import { Dialog, DialogContent, DialogTitle, Button, Form, Icon, IconButton } from '@shopflo/ui'
import { errorToast, successToast } from 'src/lib/utils/toasters'
import { useRtoSuiteContext } from 'src/lib/contexts/RTOSuiteProvider'
import { useLocale } from 'src/lib/hooks/useLocal'
import { PrepaidShareType, SplitCodSlabType } from 'src/lib/types/rtoSuite'
import SplitCodDialogueContentProp from './SplitCodDialogueContentProp'
import { mapFormSlabsToConfigSlabs, getSplitCodPayload } from 'src/lib/utils/rto-suite'
import { useActivityLogs, EntityName } from 'src/lib/hooks/useActivityLogs'
import { useForm, useFieldArray, FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { splitCodConfigSchema, SplitCodFormValues } from 'src/lib/schemas/splitCodSchema'
import { Cube, X } from '@phosphor-icons/react'
import { updateSplitCodConfig } from 'src/lib/core/api-methods'

interface ConfigureSplitCodDialogProps {
  openSplitConfig: boolean
  setOpenSplitConfig: (value: boolean) => void
}

const ConfigureSplitCodDialog: React.FC<ConfigureSplitCodDialogProps> = ({ openSplitConfig, setOpenSplitConfig }) => {
  const { sendActivityLog } = useActivityLogs()
  const { t } = useLocale()
  const splitCodResponse = useRtoSuiteContext(context => context?.state?.splitCodConfig)
  const setSplitCodConfig = useRtoSuiteContext(context => context?.actions?.setSplitCodConfig)
  const setInitialSplitCodConfig = useRtoSuiteContext(context => context?.actions?.setInitialSplitCodConfig)

  const defaultValues: SplitCodFormValues = useMemo(
    () => ({
      slabs:
        splitCodResponse?.slabs?.map(slab => ({
          minOrderValue: slab.minOrderValue,
          maxOrderValue: slab.maxOrderValue ?? null,
          prepaidShare: slab.prepaidShare,
          prepaidShareType: slab.prepaidShareType,
          isCODChargesExcluded: slab.isCODChargesExcluded ?? false
        })) || []
    }),
    [splitCodResponse]
  )

  const form = useForm<SplitCodFormValues>({
    resolver: zodResolver(splitCodConfigSchema),
    defaultValues,
    values: defaultValues,
    mode: 'onChange'
  })

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'slabs'
  })

  const handleClose = () => {
    setOpenSplitConfig(false)
    form.reset()
  }

  const onSubmit = async (data: SplitCodFormValues) => {
    try {
      const newSlabs = mapFormSlabsToConfigSlabs(data.slabs)

      const payload = getSplitCodPayload(splitCodResponse?.isEnabled ?? false, newSlabs)

      await updateSplitCodConfig(payload)

      const updatedConfig = {
        ...splitCodResponse,
        slabs: newSlabs
      }

      setSplitCodConfig(updatedConfig)
      setInitialSplitCodConfig(updatedConfig)

      const activityLogPayload = {
        entityName: EntityName.RTO_SUITE,
        actionName: 'edited configuration'
      }
      sendActivityLog(activityLogPayload)
      successToast('Configuration saved successfully')
      handleClose()
    } catch (error) {
      console.error('Error saving split COD config', error)
      errorToast('Failed to save configuration')
    }
  }

  const handleCreateSlab = () => {
    const currentSlabs = form.getValues('slabs')
    const lastSlab = currentSlabs[currentSlabs.length - 1]

    const newMin = (lastSlab?.maxOrderValue || 0) + 1
    append({
      minOrderValue: newMin,
      maxOrderValue: null,
      prepaidShare: 0,
      prepaidShareType: PrepaidShareType.Percentage,
      isCODChargesExcluded: false
    })
  }

  return (
    <Dialog open={openSplitConfig} onOpenChange={open => !open && handleClose()}>
      <DialogContent className='w-[600px] !rounded-2xl bg-white shadow-lg p-0 gap-0 overflow-hidden'>
        <div className='px-5 pt-4 flex items-center gap-3 justify-between shadow-[0px_8px_10px_0px_#ffffff] z-10'>
          <div className='flex items-center gap-3'>
            <div className='h-10 w-10 rounded-xl flex items-center justify-center bg-zinc-100'>
              <Icon IconComponent={Cube} size='lg' className='text-zinc-500' iconProps={{ weight: 'duotone' }} />
            </div>
            <h3 className='text-text-lg-semibold text-zinc-800'>{t('split_cod.edit_split_payment_rules')}</h3>
          </div>
          <IconButton variant='ghost' size='sm' onClick={handleClose} IconComponent={X} />
        </div>

        <div className='px-5 max-h-[70vh] overflow-y-auto py-4'>
          <FormProvider {...form}>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} id='split-cod-form'>
                <SplitCodDialogueContentProp fields={fields} remove={remove} append={handleCreateSlab} />
              </form>
            </Form>
          </FormProvider>
        </div>

        <div className='px-5 pb-4 flex justify-end gap-2 pt-1 shadow-[0px_-8px_10px_0px_rgb(255,255,255)]'>
          <Button variant='outline' onClick={handleClose} size='md'>
            {t('cancel')}
          </Button>
          <Button variant='primary' type='submit' form='split-cod-form' size='md'>
            {t('save')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
export default ConfigureSplitCodDialog
